export interface WorkOrderAccess {
  canManage: boolean;
  canViewAll: boolean;
  canViewOwn: boolean;
  canEditEmploymentForms: boolean;
}

export interface WorkOrderTab {
  key: string;
  label: string;
}

const OVERSIGHT_PATH = '/assignments/oversight';
const READINESS_PATH = '/assignments/reporting-completeness';

const isPathOrChild = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

// Tabs of the Work Orders section in display order. Each tab owns its route
// and sub-routes; the page guards stay on the routes themselves.
const TABS: Array<
  WorkOrderTab & {
    allowed: (access: WorkOrderAccess) => boolean;
    matches: (pathname: string) => boolean;
  }
> = [
  {
    key: '/my-assignments',
    label: 'WORK_ORDERS_NAV.MY',
    allowed: ({ canViewOwn }) => canViewOwn,
    matches: (pathname) => isPathOrChild(pathname, '/my-assignments'),
  },
  {
    key: '/assignments',
    label: 'WORK_ORDERS_NAV.ALL',
    allowed: ({ canManage, canViewAll }) => canManage || canViewAll,
    matches: (pathname) =>
      isPathOrChild(pathname, '/assignments') &&
      !isPathOrChild(pathname, OVERSIGHT_PATH) &&
      !isPathOrChild(pathname, READINESS_PATH),
  },
  {
    key: OVERSIGHT_PATH,
    label: 'WORK_ORDERS_NAV.OVERSIGHT',
    allowed: ({ canViewAll }) => canViewAll,
    matches: (pathname) => isPathOrChild(pathname, OVERSIGHT_PATH),
  },
  {
    key: READINESS_PATH,
    label: 'WORK_ORDERS_NAV.READINESS',
    allowed: ({ canManage, canViewAll }) => canManage || canViewAll,
    matches: (pathname) => isPathOrChild(pathname, READINESS_PATH),
  },
  {
    key: '/employment-forms',
    label: 'WORK_ORDERS_NAV.EMPLOYMENT_FORMS',
    allowed: ({ canEditEmploymentForms }) => canEditEmploymentForms,
    matches: (pathname) => isPathOrChild(pathname, '/employment-forms'),
  },
];

export const isWorkOrdersPath = (pathname: string) =>
  TABS.some(({ matches }) => matches(pathname));

export const getWorkOrderTabs = (access: WorkOrderAccess): WorkOrderTab[] =>
  TABS.filter(({ allowed }) => allowed(access)).map(({ key, label }) => ({
    key,
    label,
  }));

export const getActiveWorkOrderTab = (pathname: string) =>
  TABS.find(({ matches }) => matches(pathname))?.key;

// Where the top-level Work Orders item leads: All assignments for issuers,
// managers and accounting (owner, 24.10, D8), otherwise the first tab the
// user may use, or nowhere when no tab is left, as for an
// employment-contract employee.
export const getWorkOrdersHome = (access: WorkOrderAccess) =>
  access.canManage || access.canViewAll
    ? '/assignments'
    : getWorkOrderTabs(access)[0]?.key;
